// ---------------------------------------------------------------------------
// Choosing who a sale is for.
//
// Most sales are to somebody who walks in and pays, and those need no customer
// at all. This is for the ones that do: a delivery, a restaurant's standing
// order, or anything going on an account.
//
// Credit figures are shown only when the server sent them, which it does only
// for somebody holding `customers.credit`. A cashier picking a customer for a
// delivery sees the name and phone and nothing about money.
// ---------------------------------------------------------------------------

import { useState } from 'react';
import { AlertTriangle, Search, UserRound } from 'lucide-react';
import clsx from 'clsx';
import { Modal } from '../common/Modal';
import { useI18n } from '../../i18n/I18nProvider';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useCustomers } from '../../api/customers.api';
import { EmptyState, ErrorState, SkeletonRows } from '../common/StateBlocks';
import { formatUsd, toDecimal } from '../../utils/format';
import type { Customer } from '../../types';

interface CustomerPickerProps {
  open: boolean;
  onClose: () => void;
  onChoose: (customer: Customer | null) => void;
  /// When true, somebody who cannot buy on credit is shown greyed out with the
  /// reason, rather than being silently absent.
  forCredit?: boolean;
}

export function CustomerPicker({ open, onClose, onChoose, forCredit = false }: CustomerPickerProps) {
  const { t, locale } = useI18n();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 250);

  const { data, isLoading, error, refetch } = useCustomers(
    { search: debouncedSearch || undefined },
    open,
  );

  const customers = data?.customers ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t.pos.chooseCustomer}
      size="md"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t.common.cancel}
          </button>
          {/* Deliberately available: a sale started for somebody by name can
              go back to being an ordinary walk-in. */}
          {!forCredit ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                onChoose(null);
                onClose();
              }}
            >
              {t.pos.noCustomerChosen}
            </button>
          ) : null}
        </>
      }
    >
      <div className="relative mb-3">
        <Search
          className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-muted"
          aria-hidden="true"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t.pos.customerSearch}
          aria-label={t.pos.customerSearch}
          className="input ps-10"
        />
      </div>

      {isLoading ? (
        <SkeletonRows rows={4} />
      ) : error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : customers.length === 0 ? (
        <EmptyState
          title={t.customers.empty}
          description={t.customers.emptyHint}
          icon={<UserRound className="h-6 w-6" />}
        />
      ) : (
        <ul className="max-h-[50vh] space-y-1.5 overflow-y-auto">
          {customers.map((customer) => {
            // Only meaningful when the server sent credit figures at all.
            const blocked = forCredit && customer.balance ? !customer.balance.canBuyOnCredit : false;

            return (
              <li key={customer.id}>
                <button
                  type="button"
                  disabled={blocked}
                  onClick={() => {
                    onChoose(customer);
                    onClose();
                  }}
                  className={clsx(
                    'w-full rounded-control border p-3 text-start transition-colors',
                    blocked
                      ? 'cursor-not-allowed border-line bg-surface-sunken opacity-60'
                      : 'border-line-strong bg-surface hover:bg-surface-soft',
                  )}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-ink">{customer.name}</span>
                    <span className="numeric text-xs text-ink-muted" dir="ltr">
                      {customer.code}
                    </span>
                  </div>

                  <div className="mt-0.5 flex flex-wrap items-baseline justify-between gap-2 text-xs">
                    <span className="text-ink-muted" dir="ltr">
                      {customer.phone ?? '—'}
                      <span className="mx-1.5">·</span>
                      <span dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                        {t.customers[`type${customer.type}` as const]}
                      </span>
                    </span>

                    {/* Absent entirely without customers.credit. */}
                    {customer.balance ? (
                      <span className="text-ink-muted">
                        {toDecimal(customer.balance.balanceUsd).greaterThan(0) ? (
                          <>
                            {t.credit.balance}{' '}
                            <span className="numeric font-semibold text-burgundy-red">
                              {formatUsd(customer.balance.balanceUsd)}
                            </span>
                          </>
                        ) : (
                          t.credit.settled
                        )}
                      </span>
                    ) : null}
                  </div>

                  {blocked && customer.balance?.blockedReason ? (
                    <p className="mt-1.5 flex items-start gap-1.5 text-xs text-burgundy-red">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                      {customer.balance.blockedReason}
                    </p>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Modal>
  );
}
